import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Session, VerbatimSelection } from '@/domain/schema/v1';
import type { ExcerptCandidate } from '@/domain/transcript/excerpts';
import { TranscriptService } from '@/services/transcripts';
import { useLoad } from '../../hooks/useLoad';
import { useRequiredSession } from '../../state/session';
import { useService } from '../../hooks/useService';
import { fmtMs, RoleBadge, TranscriptViewer } from '../../components/TranscriptViewer';

export function useTranscripts() {
  const s = useRequiredSession();
  return useMemo(() => new TranscriptService(s.repo, s.ctx), [s]);
}

/** 회기 자료 처리 결과 + 전사 보기 + 발췌 추천 (UX_FLOW §9) */
export function TranscriptSection({ session, onChanged }: { session: Session; onChanged: () => void }) {
  const ts = useTranscripts();
  const svc = useService();
  const [params] = useSearchParams();
  const focusSeg = params.get('seg');
  const { data, reload } = useLoad(async () => {
    const working = await ts.getWorking(session);
    const review = await ts.reviewItems(session);
    const formulation = await svc.currentFormulation(session.clientId);
    const hyps = formulation
      ? [
          ...(formulation.beliefHypotheses ?? []),
          ...(formulation.coreCognitiveThemes ?? []),
          ...(formulation.relationalPatterns ?? []),
        ].map((h) => h.text)
      : [];
    const selections = await ts.listSelections({ sessionId: session.id });
    return { working, review, hyps, selections };
  }, [ts, session.id, session.revision]);

  const p = session.processingState;
  const st = data?.working?.stats;
  const row = (ok: boolean, text: string) => (
    <li className={ok ? 'done' : ''}>
      {ok ? '✓' : '·'} {text}
    </li>
  );

  return (
    <>
      <div className="card">
        <h2>회기 자료</h2>
        <ul className="checklist">
          {row(p.filesSaved, '전사·메모 저장')}
          {session.samsungTranscriptId && row(p.samsungParsed, `Galaxy 전사 읽음${st ? ` (${st.galaxyTurns}턴)` : ''}`)}
          {session.appleTranscriptId && row(p.appleParsed, `iPhone 전사 읽음${st ? ` (${st.iphoneBlocks}블록)` : ''}`)}
          {session.samsungTranscriptId && session.appleTranscriptId && row(p.transcriptsAligned, '두 전사 내용 맞춤')}
          {session.samsungTranscriptId &&
            session.appleTranscriptId &&
            p.workingTranscriptCreated &&
            !p.transcriptsAligned && (
              <li className="warn">
                다른 기기의 전사가 처리 후에 도착해 작업 전사에는 들어가지 않았습니다. 원문은 보관되어 있습니다.
              </li>
            )}
          {row(p.rolesInferred, '상담자/내담자 자동 구분')}
          {row(p.workingTranscriptCreated, '작업 전사 생성')}
        </ul>
        {data && data.review.length > 0 && (
          <div className="banner warn-banner">
            ⚠ 확인할 부분 {data.review.length}곳
            <Link className="button" to={`/sessions/${session.id}/verify`}>
              확인하기
            </Link>
          </div>
        )}
        {p.workingTranscriptCreated && data?.review.length === 0 && session.status === 'verification' && (
          <button type="button" onClick={() => void svc.completeVerification(session.id).then(onChanged)}>
            확인 완료로 표시
          </button>
        )}
      </div>

      {data?.working && (
        <details
          className="card"
          open={!!focusSeg}
          ref={(el) => void (focusSeg && el?.scrollIntoView({ block: 'start' }))}
        >
          <summary>
            <strong>전사 보기</strong> <span className="muted small">{data.working.segments.length}줄</span>
          </summary>
          <TranscriptViewer
            segments={data.working.segments}
            onSetRole={(id, role) => void ts.setRole(session, id, role).then(reload)}
            {...(focusSeg ? { focusId: focusSeg } : {})}
          />
        </details>
      )}

      {data?.working && <Excerpts session={session} hyps={data.hyps} selections={data.selections} onSaved={reload} />}
    </>
  );
}

function Excerpts({
  session,
  hyps,
  selections,
  onSaved,
}: {
  session: Session;
  hyps: string[];
  selections: VerbatimSelection[];
  onSaved: () => void;
}) {
  const savedCount = selections.filter((v) => v.status === 'selected' && !v.deletedAt).length;
  const ts = useTranscripts();
  // 문서 화면의 '직접 지정하기'에서 오면 (?ranges=1) 이 칸을 펼쳐 바로 보여준다
  const [params] = useSearchParams();
  const fromDocs = params.get('ranges') === '1';
  const [open, setOpen] = useState(fromDocs);
  const { data } = useLoad(async () => (open ? ts.recommend(session, hyps) : null), [ts, open, session.revision]);
  const [saved, setSaved] = useState<Set<number>>(new Set());
  return (
    <details
      className="card"
      open={fromDocs || undefined}
      ref={(el) => void (fromDocs && el?.scrollIntoView({ block: 'start' }))}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary>
        <strong>주요 교환 발췌 · 축어록 추천</strong> <span className="muted small">저장 {savedCount}개</span>
      </summary>
      <p className="muted small">
        사례보고서의 축어록·주요 교환으로 쓸 수 있는 구간입니다. 실제 발언 그대로이며, 화자가 불확실한 줄은 "확인
        필요"로 표시됩니다.
      </p>
      <Ranges session={session} selections={selections} onChanged={onSaved} />
      {!data ? (
        <p className="muted">불러오는 중…</p>
      ) : (
        <>
          <h2>주요 교환</h2>
          {data.exchanges.length === 0 && <p className="muted">추천할 구간이 없습니다.</p>}
          {data.exchanges.map((c, k) => (
            <Candidate
              key={`e${k}`}
              c={c}
              saved={saved.has(k)}
              onSave={() => void ts.saveSelection(session, c).then(() => (setSaved(new Set(saved).add(k)), onSaved()))}
            />
          ))}
          <h2>축어록 후보 (약 12분)</h2>
          {data.verbatims.length === 0 && <p className="muted">회기가 짧아 축어록 후보가 없습니다.</p>}
          {data.verbatims.map((c, k) => (
            <Candidate
              key={`v${k}`}
              c={c}
              saved={saved.has(100 + k)}
              compact
              onSave={() =>
                void ts.saveSelection(session, c).then(() => (setSaved(new Set(saved).add(100 + k)), onSaved()))
              }
            />
          ))}
        </>
      )}
    </details>
  );
}

const parseClock = (t: string): number | null => {
  const m = /^\s*(?:(\d+):)?(\d{1,3}):(\d{2})\s*$/.exec(t);
  if (!m) return null;
  return ((Number(m[1] ?? 0) * 60 + Number(m[2])) * 60 + Number(m[3])) * 1000;
};

/** 저장한 구간 · AI 추천 구간 채택 · 직접 구간 지정 */
function Ranges({
  session,
  selections,
  onChanged,
}: {
  session: Session;
  selections: VerbatimSelection[];
  onChanged: () => void;
}) {
  const ts = useTranscripts();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const saved = selections.filter((v) => v.status === 'selected');
  const ai = selections.filter((v) => v.status === 'candidate' && v.proposedBy === 'ai');
  const s = parseClock(start);
  const e = parseClock(end);
  const minutes = s !== null && e !== null && e > s ? Math.round((e - s) / 60000) : null;
  return (
    <div className="stack">
      {saved.length > 0 && (
        <>
          <h2>저장한 구간</h2>
          <ul className="list">
            {saved.map((v) => (
              <li key={v.id} className="list-row">
                <span>
                  {fmtMs(v.startMs)}–{fmtMs(v.endMs)} · {v.processNotes ?? '발췌'}
                  {v.selectionReason && <span className="muted small"> — {v.selectionReason}</span>}
                </span>
                <button type="button" onClick={() => void ts.removeSelection(v.id).then(onChanged)}>
                  빼기
                </button>
              </li>
            ))}
          </ul>
          <p className="muted small">
            내담자 상세 &gt; 문서에서 축어록 문서로 만들거나, 사례보고서에 자동으로 들어갑니다.
          </p>
        </>
      )}
      {ai.length > 0 && (
        <>
          <h2>AI 추천 구간</h2>
          {ai.map((v) => (
            <div key={v.id} className="item-block row">
              <span>
                <strong>
                  {fmtMs(v.startMs)}–{fmtMs(v.endMs)}
                </strong>{' '}
                <span className="muted small">{v.selectionReason ?? ''}</span>
              </span>
              <button type="button" onClick={() => void ts.adoptCandidate(v.id).then(onChanged)}>
                채택
              </button>
            </div>
          ))}
        </>
      )}
      <h2>직접 구간 지정</h2>
      <div className="row">
        <input
          aria-label="시작 시각"
          placeholder="시작 12:00"
          inputMode="numeric"
          value={start}
          onChange={(x) => setStart(x.target.value)}
        />
        <span>~</span>
        <input
          aria-label="끝 시각"
          placeholder="끝 25:00"
          inputMode="numeric"
          value={end}
          onChange={(x) => setEnd(x.target.value)}
        />
        {minutes !== null && (
          <span className="muted small">
            약 {minutes}분{minutes < 10 || minutes > 15 ? ' (보통 10~15분)' : ''}
          </span>
        )}
      </div>
      <input
        aria-label="선택 이유"
        placeholder="선택 이유 (선택)"
        value={reason}
        onChange={(x) => setReason(x.target.value)}
      />
      {err && <p className="warn small">{err}</p>}
      <button
        type="button"
        disabled={s === null || e === null}
        onClick={() => {
          setErr(null);
          void ts
            .saveRange(session, s!, e!, reason)
            .then(() => {
              setStart('');
              setEnd('');
              setReason('');
              onChanged();
            })
            .catch((x: Error) => setErr(x.message));
        }}
      >
        이 구간 저장
      </button>
    </div>
  );
}

function Candidate({
  c,
  saved,
  onSave,
  compact,
}: {
  c: ExcerptCandidate;
  saved: boolean;
  onSave: () => void;
  compact?: boolean;
}) {
  const lines = compact ? c.lines.slice(0, 6) : c.lines;
  return (
    <div className="item-block stack">
      <div className="row small">
        <strong>
          {fmtMs(c.startMs)}–{fmtMs(c.endMs)}
        </strong>
        {c.hasRisk && <span className="badge badge-review">위험 관련</span>}
        {c.needsCheck && <span className="badge badge-missing">확인 필요 줄 있음</span>}
      </div>
      <p className="muted small">{c.reasons.join(' · ')}</p>
      <div className="excerpt">
        {lines.map((l) => (
          <p key={l.segmentId}>
            <RoleBadge seg={{ clinicalRole: l.role, roleConfidence: l.uncertain ? 0.6 : 0.9, roleSource: 'auto' }} />{' '}
            {l.text}
          </p>
        ))}
        {compact && c.lines.length > lines.length && (
          <p className="muted small">… 외 {c.lines.length - lines.length}줄</p>
        )}
      </div>
      <p className="small">수퍼비전 질문: {c.supervisionQuestions.join(' / ')}</p>
      <button type="button" disabled={saved} onClick={onSave}>
        {saved ? '사례보고서용으로 저장됨' : '사례보고서에 사용'}
      </button>
    </div>
  );
}
